import { FormEvent, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { App, Phase } from '../lib/types';
import { PHASE_LABEL, PHASES } from '../lib/types';

interface Props {
  holdingId: string;
  app?: App;
  onClose: () => void;
  onSaved: () => void;
}

function slugify(s: string) {
  return s.toLowerCase().trim()
    .replace(/[áàä]/g, 'a').replace(/[éèë]/g, 'e').replace(/[íìï]/g, 'i')
    .replace(/[óòö]/g, 'o').replace(/[úùü]/g, 'u').replace(/ñ/g, 'n')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

export default function AppModal({ holdingId, app, onClose, onSaved }: Props) {
  const [name, setName] = useState(app?.name ?? '');
  const [slug, setSlug] = useState(app?.slug ?? '');
  const [description, setDescription] = useState(app?.description ?? '');
  const [domain, setDomain] = useState(app?.domain ?? '');
  const [phase, setPhase] = useState<Phase>(app?.phase ?? 'discovery');
  const [stackNotes, setStackNotes] = useState(app?.stack_notes ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload = {
      holding_id: holdingId,
      name: name.trim(),
      slug: slug.trim() || slugify(name),
      description: description.trim() || null,
      domain: domain.trim() || null,
      phase,
      stack_notes: stackNotes.trim() || null,
    };
    try {
      if (app?.id) {
        const { error } = await supabase.from('roadmap_apps').update(payload).eq('id', app.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('roadmap_apps').insert(payload);
        if (error) throw error;
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setSaving(false);
    }
  }

  async function onDelete() {
    if (!app?.id) return;
    if (!confirm(`Eliminar app "${app.name}" y todas sus iniciativas?`)) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('roadmap_apps').delete().eq('id', app.id);
      if (error) throw error;
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al eliminar');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <form
        onSubmit={onSubmit}
        onClick={(e) => e.stopPropagation()}
        className="glass rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-auto"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold">{app ? 'Editar app' : 'Nueva app'}</h3>
          <button type="button" className="ml-auto btn text-xs" onClick={onClose}>✕</button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Nombre</label>
            <input
              className="input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (!app) setSlug(slugify(e.target.value));
              }}
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label">Slug</label>
            <input className="input" value={slug} onChange={(e) => setSlug(e.target.value)} required />
          </div>
        </div>

        <div>
          <label className="label">Descripción</label>
          <textarea
            className="input min-h-[60px]"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Dominio</label>
            <input
              className="input"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="ej: belucid.life"
            />
          </div>
          <div>
            <label className="label">Fase</label>
            <select className="input" value={phase} onChange={(e) => setPhase(e.target.value as Phase)}>
              {PHASES.map((p) => <option key={p} value={p}>{PHASE_LABEL[p]}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="label">Stack / notas</label>
          <textarea
            className="input min-h-[60px]"
            value={stackNotes}
            onChange={(e) => setStackNotes(e.target.value)}
          />
        </div>

        {error && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2">
          {app && (
            <button type="button" className="btn text-xs text-rose-300" onClick={onDelete} disabled={saving}>
              Eliminar
            </button>
          )}
          <button type="button" className="btn ml-auto" onClick={onClose}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
